import { useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, Globe, MoreHorizontal, Search, ShieldCheck } from 'lucide-react';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { useResellers, useAdminBusinesses, useAdminDomains } from '../../api/admin';
import { formatDate } from '../../lib/format';

const SCOPE_LABEL = { reseller: 'Reseller', business: 'Business' };

/** Cross-reseller domains list. */
export function Domains() {
  const { data: domains = [] } = useAdminDomains();
  const [scopeFilter, setScopeFilter] = useState<string>('all');
  const [verifiedFilter, setVerifiedFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return domains.filter((d) => {
      if (scopeFilter !== 'all' && d.scope !== scopeFilter) return false;
      if (verifiedFilter === 'verified' && !d.isVerified) return false;
      if (verifiedFilter === 'pending' && d.isVerified) return false;
      if (search && !d.hostname.includes(search.toLowerCase()) && !d.ownerName.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [domains, scopeFilter, verifiedFilter, search]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title={<span>Domains <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">({domains.length})</span></span>}
        subtitle="Every reseller and business custom domain across the platform. Resellers add their own domains from their portal."
      />
      {/*
        The "Add domain" action was REMOVED, not hidden. Its modal walked through
        hostname → verify → "Domain added" and then simply closed: `done()` set a step
        and called close(), with no API call anywhere. An admin was told a domain had
        been created when nothing had been written.

        There is no admin-scoped create endpoint to wire it to — only the reseller's own
        POST /reseller/domains, which cannot target an arbitrary reseller or business. A
        real admin version is a feature (scope choice, ownership, DNS/TLS lifecycle), so
        it is tracked in the deferred backlog rather than left here pretending to work.
      */}

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <Filter
            label="Scope"
            value={scopeFilter === 'all' ? 'All' : SCOPE_LABEL[scopeFilter as 'reseller' | 'business']}
            options={[
              { v: 'all', l: 'All' },
              { v: 'reseller', l: 'Reseller' },
              { v: 'business', l: 'Business' },
            ]}
            onChange={setScopeFilter}
          />
          <Filter
            label="Verification"
            value={verifiedFilter === 'all' ? 'All' : verifiedFilter.charAt(0).toUpperCase() + verifiedFilter.slice(1)}
            options={[
              { v: 'all', l: 'All' },
              { v: 'verified', l: 'Verified' },
              { v: 'pending', l: 'Pending' },
            ]}
            onChange={setVerifiedFilter}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search hostname or owner…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Hostname</th>
                <th className="text-left font-medium px-4 py-3">Scope</th>
                <th className="text-left font-medium px-4 py-3">Owner</th>
                <th className="text-left font-medium px-4 py-3">Verification</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">TLS</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Added</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {filtered.map((d) => (
                <tr key={d.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3">
                    <div className="flex items-center gap-2.5">
                      <Globe className="w-4 h-4 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-950 dark:text-zinc-50 mono text-xs flex items-center gap-2">
                          {d.hostname}
                          {d.isPrimary && <span className="text-[10px] uppercase tracking-wide bg-zinc-950 dark:bg-zinc-50 text-white dark:text-zinc-950 px-1.5 py-px rounded">Primary</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{SCOPE_LABEL[d.scope]}</td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{d.ownerName}</td>
                  <td className="px-4 py-3">
                    <Badge tone={d.isVerified ? 'success' : 'warning'}>
                      {d.isVerified ? 'Verified' : 'Pending'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <Badge tone={d.tlsStatus === 'issued' ? 'success' : d.tlsStatus === 'pending' ? 'warning' : 'error'}>
                      {d.tlsStatus.charAt(0).toUpperCase() + d.tlsStatus.slice(1)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 tabular-nums hidden lg:table-cell">{formatDate(d.addedAt, { compact: true })}</td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    <button className="w-8 h-8 grid place-items-center rounded-md text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400">
          Showing <span className="tabular-nums">{filtered.length}</span> of <span className="tabular-nums">{domains.length}</span>
        </div>
      </Card>
    </div>
  );
}


function Filter({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[160px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className="block w-full text-left px-3 py-1.5 text-xs text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
